import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowLeft,
    Target,
    Compass,
    Sparkles,
    Quote,
    UserCheck,
    TrendingUp,
    Store,
    ShieldAlert,
    HeartHandshake,
    Cpu,
    ChevronRight,
    ChevronDown,
    Eye,
    Terminal,
    Scale,
    Layers,
    Globe2,
    Fingerprint
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUs = ({ isDark, lang }) => {
    const [activeTab, setActiveTab] = useState('mission');
    const [selectedPillar, setSelectedPillar] = useState(0);
    const [activeGenesis, setActiveGenesis] = useState(2);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 1. Bento Grid Interactivo
    const pillars = [
        {
            id: "kanan",
            tag: lang === 'es' ? "SEGURIDAD CÍVICA // KANAN" : "CIVIC SAFETY // KANAN",
            title: lang === 'es' ? "Mitigación de Riesgos y Protección Colectiva" : "Risk Mitigation & Community Safety",
            highlight: lang === 'es' ? "Mapas de Calor en Vivo" : "Live Threat Mapping",
            desc: lang === 'es'
                ? "Kanan mapea de forma geoespacial incidencias y zonas de alerta en tiempo real. Permite que vecinos y transeúntes tomen rutas seguras mediante reportes ciudadanos rápidos y anónimos."
                : "Kanan geospatially maps real-time incidents and risk zones. It empowers citizens to navigate safely through fast, anonymous community reports.",
            icon: ShieldAlert,
            color: "from-blue-600/20 via-blue-500/10 to-transparent",
            accent: "text-blue-500",
            border: "border-blue-500/30"
        },
        {
            id: "blebe",
            tag: lang === 'es' ? "PROCESOS & SELECCIÓN // BLEBE" : "VETTING // BLEBE",
            title: lang === 'es' ? "Optimización de Tiempos sin Reemplazar al Humano" : "Time Optimization Without Replacing People",
            highlight: lang === 'es' ? "Eficiencia en Selección" : "Smart Recruitment",
            desc: lang === 'es'
                ? "Blebe es una herramienta diseñada para acompañar y apoyar los procesos de selección de personal, agilizando etapas operativas clave para ahorrarle tiempo valioso al reclutador."
                : "Blebe is built to assist hiring workflows, streamlining key operational steps to save valuable time for recruiters while keeping human insight at the center.",
            icon: UserCheck,
            color: "from-indigo-600/20 via-indigo-500/10 to-transparent",
            accent: "text-indigo-500",
            border: "border-indigo-500/30"
        },
        {
            id: "finance",
            tag: lang === 'es' ? "SALUD FINANCIERA // NIYA" : "FINANCIAL CLARITY // NIYA",
            title: lang === 'es' ? "Certeza Económica en la Toma de Decisiones" : "Financial Clarity for Real Decisions",
            highlight: lang === 'es' ? "Claridad de Flujo Real" : "Real Cash Flow",
            desc: lang === 'es'
                ? "Niya es una herramienta intuitiva diseñada para ordenar las finanzas diarias y entender el flujo real del dinero, ayudando a personas y trabajadores independientes a tomar decisiones económicas con certidumbre y sin lenguaje bancario confuso."
                : "Niya is an intuitive platform built to organize daily personal finances and track real cash flow, enabling individuals and independent workers to make sound economic choices without complex jargon.",
            icon: TrendingUp,
            color: "from-emerald-600/20 via-emerald-500/10 to-transparent",
            accent: "text-emerald-500",
            border: "border-emerald-500/30"
        },
        {
            id: "marchantite",
            tag: lang === 'es' ? "COMERCIO LOCAL // MARCHANTITE" : "LOCAL TRADE // MARCHANTITE",
            title: lang === 'es' ? "Herramientas Modernas para Mercados Tradicionales" : "Modern Digital Tools for Local Markets",
            highlight: lang === 'es' ? "Equidad Competitiva" : "Market Fairness",
            desc: lang === 'es'
                ? "Marchantite digitaliza a tienditas, mercados sobre ruedas y pequeños oficios para que reciban pedidos móviles y retengan a su clientela frente a plataformas corporativas transnacionales."
                : "Marchantite connects traditional corner shops and open-air markets with mobile ordering tools, enabling them to protect their local customer base against corporate delivery apps.",
            icon: Store,
            color: "from-amber-600/20 via-amber-500/10 to-transparent",
            accent: "text-amber-500",
            border: "border-amber-500/30"
        }
    ];

    // 2. Cronología Génesis
    const genesisTimeline = [
        {
            year: "2019",
            phaseTag: lang === 'es' ? "Fase 01" : "Phase 01",
            phase: lang === 'es' ? "Semillero Académico" : "Academic Lab",
            title: lang === 'es' ? "Ideación y Código Experimental" : "Ideation & Experimental Code",
            badge: "University Roots",
            desc: lang === 'es'
                ? "TDSMX surge en aulas de ingeniería como un colectivo multidisciplinario. El propósito inicial: explorar arquitecturas de software no convencionales para resolver problemas reales del entorno estudiantil y vecinal."
                : "TDSMX started in engineering classrooms as an interdisciplinary cohort aimed at exploring software architectures to solve genuine community problems.",
            specs: ["Arquitecturas Móviles", "Laboratorio Experimental", "Algoritmos Cívicos"]
        },
        {
            year: "2021",
            phaseTag: lang === 'es' ? "Fase 02" : "Phase 02",
            phase: lang === 'es' ? "Maduración Técnica" : "Engineering Core",
            title: lang === 'es' ? "Desarrollo Nativo & Infraestructura Cloud" : "Native Builds & Cloud Scale",
            badge: "Systems Foundation",
            desc: lang === 'es'
                ? "Transición de prototipos a stacks móviles nativos (iOS & Android). Establecimos pipelines robustos de sincronización de datos, telemetría y modelos geoespaciales de bajo consumo de batería."
                : "Shifted from prototypes to native mobile pipelines (iOS & Android), engineering low-latency geospatial services and battery-efficient architecture.",
            specs: ["KMP & Native Runtimes", "Geolocalización Segura", "Microservicios"]
        },
        {
            year: "2024 - Hoy",
            phaseTag: lang === 'es' ? "Fase 03" : "Phase 03",
            phase: lang === 'es' ? "Ecosistema Propio" : "Venture Ecosystem",
            title: lang === 'es' ? "Consolidación de Iniciativas Propias" : "Proprietary Platform Venture",
            badge: "Full Ecosystem",
            desc: lang === 'es'
                ? "Evolución definitiva a firma tecnológica. Dejamos atrás el desarrollo a terceros para enfocarnos exclusivamente en incubar y operar nuestras propias plataformas: Kanan, Blebe, Marchantite y Niya."
                : "Definitive transition to an independent tech firm. We stopped third-party contract work to exclusively build and operate our own ecosystem: Kanan, Blebe, Marchantite, and finance suites.",
            specs: ["Compliance Legal", "Despliegue Multi-Plataforma", "Gobernanza de Datos"]
        }
    ];

    // 3. Principios
    const values = [
        {
            title: lang === 'es' ? "Asistir, Jamás Sustituir" : "Assist, Never Replace",
            sub: lang === 'es' ? "Diseñamos software para potenciar el juicio de las personas, no para reemplazar su criterio." : "We build tools to empower human judgment, not supplant it.",
            icon: HeartHandshake
        },
        {
            title: lang === 'es' ? "Certeza Inmediata" : "Instant Certainty",
            sub: lang === 'es' ? "Información clara y transparente para tomar decisiones informadas en minutos." : "Clear, verifiable signals for fast decision-making.",
            icon: Cpu
        },
        {
            title: lang === 'es' ? "Equidad Comercial" : "Local Fairness",
            sub: lang === 'es' ? "Llevar la mejor ingeniería a manos de pequeños comercios y trabajadores de barrio." : "Putting enterprise-grade tooling into local corner shops.",
            icon: Store
        },
        {
            title: lang === 'es' ? "Ética en Cada Línea" : "Built-in Ethics",
            sub: lang === 'es' ? "Plataformas blindadas jurídicamente que cuidan la integridad y privacidad del usuario." : "Legally guarded platforms that prioritize user privacy and safety.",
            icon: Scale
        }
    ];

    // 4. Liderazgo
    const leadership = [
        {
            name: "Engelbert Loera",
            role: lang === 'es' ? "Co-Fundador & Director General" : "Co-Founder & Managing Director",
            objective: lang === 'es'
                ? "Misión: Dirigir la visión estratégica, la ejecución técnica y el desarrollo integral de las plataformas que componen el ecosistema TDSMX."
                : "Mission: Lead strategic vision, technical execution, and operational deployment across all TDSMX platforms.",
            motto: lang === 'es'
                ? "“La tecnología solo tiene sentido cuando resuelve un problema real y le devuelve el control y el tiempo a las personas.”"
                : "“Technology only makes sense when it solves a real problem and hands control and time back to people.”",
            image: "/team/ceo.jpg",
            badge: "Co-Founder & Executive"
        },
        {
            name: "Mia Loera",
            role: lang === 'es' ? "Dirección Legal, Privacidad & Cumplimiento" : "Chief Legal Officer & Data Governance",
            objective: lang === 'es'
                ? "Misión: Blindar la privacidad ciudadana, el anonimato en reportes y la legitimidad operativa de cada app ante plataformas globales."
                : "Mission: Safeguard citizen privacy, anonymous reporting, and platform regulatory compliance across global app ecosystems.",
            motto: lang === 'es'
                ? "“La verdadera innovación tecnológica exige proteger los derechos y la privacidad desde la primera línea de código.”"
                : "“True technological innovation demands protecting privacy and rights right from the first line of code.”",
            image: "/team/legal.jpg",
            badge: "Legal Governance & Ethics"
        }
    ];

    return (
        <div className={`min-h-screen pt-28 sm:pt-32 pb-24 px-4 sm:px-6 transition-colors duration-700 overflow-hidden ${
            isDark ? 'bg-[#0A0F1A] text-white' : 'bg-[#F8FAFC] text-slate-900'
        }`}>

            {/* Fondos ambientales */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <div className={`absolute top-20 left-1/4 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-[130px] opacity-25 ${
                    isDark ? 'bg-blue-600' : 'bg-blue-300'
                }`} />
                <div className={`absolute bottom-20 right-1/4 w-72 sm:w-96 h-72 sm:h-96 rounded-full blur-[140px] opacity-20 ${
                    isDark ? 'bg-indigo-600' : 'bg-indigo-300'
                }`} />
            </div>

            <div className="container mx-auto max-w-6xl relative z-10">

                {/* BOTÓN REGRESAR */}
                <Link
                    to="/"
                    className={`inline-flex items-center gap-2 mb-8 sm:mb-12 font-black uppercase text-[10px] tracking-widest transition-all hover:-translate-x-1.5 ${
                        isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-blue-600'
                    }`}
                >
                    <ArrowLeft size={16} /> {lang === 'es' ? 'Volver al Inicio' : 'Back to Home'}
                </Link>

                {/* HERO */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-left mb-16"
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 mb-6">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
                        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-400">
                            {lang === 'es' ? 'TECNOLOGÍA QUE APOYA, NO SUSTITUYE' : 'TECH THAT EMPOWERS HUMANS'}
                        </span>
                    </div>

                    <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter italic mb-6 max-w-4xl leading-tight">
                        {lang === 'es' ? 'Herramientas Digitales para la Vida Real' : 'Practical Tools for Real-World Life'}
                    </h1>
                    <p className="text-base sm:text-xl font-bold opacity-75 max-w-3xl leading-relaxed">
                        {lang === 'es'
                            ? 'En TDSMX construimos aplicaciones móviles para aportar certeza: mapear zonas de riesgo urbano, agilizar la selección laboral, dar claridad a las finanzas y empoderar al comercio tradicional.'
                            : 'At TDSMX we develop mobile platforms to deliver real certainty: mapping urban risk zones, speeding up trusted hiring, clarifying finances, and empowering local merchants.'}
                    </p>
                </motion.div>

                {/* SECCIÓN CORREGIDA: IDENTIDAD & SIMBOLOGÍA (EL OSO COMO SÍMBOLO DE DIVERSIDAD Y UNIDAD) */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-24 sm:mb-28 text-left"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Fingerprint size={16} className="text-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                            {lang === 'es' ? 'IDENTIDAD INSTITUCIONAL' : 'BRAND IDENTITY'}
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-8">
                        {lang === 'es' ? 'La Simbología del Oso en TDSMX' : 'The Bear Symbolism at TDSMX'}
                    </h2>

                    <div className={`p-8 sm:p-12 lg:p-14 rounded-[2.5rem] sm:rounded-[3rem] border backdrop-blur-3xl relative overflow-hidden ${
                        isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-xl shadow-blue-500/5'
                    }`}>
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                            
                            {/* Insignia / Logotipo Centrado y Balanceado */}
                            <div className="lg:col-span-4 flex flex-col items-center justify-center self-center text-center">
                                <div className="relative group flex items-center justify-center">
                                    <div className={`absolute w-52 h-52 sm:w-60 sm:h-60 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-all duration-500 ${
                                        isDark ? 'bg-blue-600' : 'bg-blue-400'
                                    }`} />
                                    
                                    <div className={`relative w-48 h-48 sm:w-56 sm:h-56 rounded-full p-3.5 border flex items-center justify-center backdrop-blur-md transition-transform duration-500 group-hover:scale-105 shadow-2xl ${
                                        isDark 
                                            ? 'bg-gradient-to-b from-blue-950/40 to-slate-900/80 border-blue-500/30' 
                                            : 'bg-gradient-to-b from-blue-50 to-blue-100/60 border-blue-300/60'
                                    }`}>
                                        <img
                                            src="/logo512.png"
                                            alt="TDSMX Bear Emblem"
                                            className="w-full h-full object-contain rounded-full select-none pointer-events-none"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = `
                                                    <div class="w-full h-full rounded-full bg-[#1e4d7b] flex flex-col items-center justify-center p-4 text-center text-white">
                                                        <span class="text-3xl font-black mb-1">TDSMX</span>
                                                        <span class="text-[9px] uppercase tracking-widest opacity-80">Bear Insignia</span>
                                                    </div>
                                                `;
                                            }}
                                        />
                                    </div>
                                </div>
                                
                                <div className="mt-5 space-y-1">
                                    <span className="text-[11px] font-black uppercase tracking-[0.25em] text-blue-500 block">
                                        {lang === 'es' ? 'IMAGOTIPO TDSMX' : 'TDSMX EMBLEM'}
                                    </span>
                                    <p className="text-[11px] font-bold opacity-60">
                                        {lang === 'es' ? 'Fuerza, Versatilidad y Comunidad' : 'Strength, Versatility & Fellowship'}
                                    </p>
                                </div>
                            </div>

                            {/* Contenido Narrativo */}
                            <div className="lg:col-span-8 flex flex-col justify-center space-y-5">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500 block mb-2">
                                        {lang === 'es' ? '¿POR QUÉ EL OSO?' : 'WHY THE BEAR?'}
                                    </span>
                                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-4 leading-tight">
                                        {lang === 'es'
                                            ? 'Diversas especies en distintos hábitats, un solo propósito común.'
                                            : 'Diverse species in different habitats, united by a single purpose.'}
                                    </h3>
                                    <p className="text-sm sm:text-base font-bold opacity-80 leading-relaxed">
                                        {lang === 'es'
                                            ? 'En el reino animal, los osos representan uno de los mayores ejemplos de adaptabilidad y variedad biológica. No existe un solo tipo de oso: desde el oso polar resistiendo en los hielos árticos, hasta el oso de anteojos trepando en la cordillera andina, el panda en los bosques de bambú o el pardo recorriendo los bosques templados. Cada especie evolucionó con fortalezas únicas para coexistir con su entorno sin perder su esencia protectora y resiliente.'
                                            : 'In nature, bears showcase unmatched biological adaptation. From polar bears thriving on ice caps and spectacled bears climbing Andean peaks, to pandas in bamboo canopies and brown bears roaming vast forests, each species develops distinct traits tailored to its environment while sharing intrinsic resilience.'}
                                    </p>
                                </div>

                                <p className="text-xs sm:text-sm font-bold opacity-75 leading-relaxed">
                                    {lang === 'es'
                                        ? 'En TDSMX adoptamos al oso como insignia porque refleja exactamente nuestra filosofía: no creemos en soluciones genéricas ni en equipos homogéneos. Desarrollamos una multiplicidad de proyectos independientes para problemáticas reales muy distintas, integrando a personas con habilidades, visiones y trayectorias diversas. Al final, sin importar qué terreno pise cada iniciativa o qué rol tenga cada persona, todos compartimos los mismos valores y formamos una sola manada.'
                                        : 'At TDSMX, the bear represents our core ethos: we reject one-size-fits-all software and homogeneous thinking. We incubate a diverse catalog of initiatives for contrasting industries, driven by people with rich, varied backgrounds. Regardless of the landscape each platform navigates, we share the same ethical code and move forward as one united team.'}
                                </p>

                                {/* Tarjetas de los 3 Pilares del Emblema */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-4 border-t border-white/10">
                                    <div className={`p-4 rounded-2xl border ${
                                        isDark ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="flex items-center gap-2 mb-1.5 text-blue-500">
                                            <Layers size={16} />
                                            <span className="text-[11px] font-black uppercase tracking-wider">
                                                {lang === 'es' ? 'Diversidad de Proyectos' : 'Product Spectrum'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] font-bold opacity-65 leading-relaxed">
                                            {lang === 'es'
                                                ? 'Herramientas cívicas, comerciales y operativas diseñadas para hábitats y necesidades reales.'
                                                : 'Platforms engineered for distinct real-world needs and distinct commercial habitats.'}
                                        </p>
                                    </div>

                                    <div className={`p-4 rounded-2xl border ${
                                        isDark ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="flex items-center gap-2 mb-1.5 text-blue-500">
                                            <Globe2 size={16} />
                                            <span className="text-[11px] font-black uppercase tracking-wider">
                                                {lang === 'es' ? 'Pluralidad de Talento' : 'Multidisciplinary'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] font-bold opacity-65 leading-relaxed">
                                            {lang === 'es'
                                                ? 'Ingeniería, marco legal y empatía social convergiendo en cada línea de código.'
                                                : 'Engineering, legal governance, and social empathy collaborating seamlessly.'}
                                        </p>
                                    </div>

                                    <div className={`p-4 rounded-2xl border ${
                                        isDark ? 'bg-white/5 border-white/5' : 'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="flex items-center gap-2 mb-1.5 text-blue-500">
                                            <HeartHandshake size={16} />
                                            <span className="text-[11px] font-black uppercase tracking-wider">
                                                {lang === 'es' ? 'Una Sola Visión' : 'One Single Team'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] font-bold opacity-65 leading-relaxed">
                                            {lang === 'es'
                                                ? 'Muchos contextos y diversas personas, pero una sola causa: tecnología que ayuda y protege.'
                                                : 'Different backgrounds and platforms, united under one mission to build tech that empowers.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </motion.div>

                {/* MISIÓN & VISIÓN */}
                <div className="mb-20 sm:mb-24">
                    <div className="flex justify-start mb-6">
                        <div className={`p-1.5 rounded-2xl border backdrop-blur-2xl inline-flex gap-2 ${
                            isDark ? 'bg-slate-900/60 border-white/10' : 'bg-slate-200/60 border-slate-300'
                        }`}>
                            <button
                                onClick={() => setActiveTab('mission')}
                                className={`relative px-5 sm:px-6 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all cursor-pointer flex items-center gap-2 ${
                                    activeTab === 'mission'
                                        ? 'text-white shadow-lg'
                                        : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                                }`}
                            >
                                {activeTab === 'mission' && (
                                    <motion.div
                                        layoutId="activeTabPill"
                                        className="absolute inset-0 bg-blue-600 rounded-xl"
                                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10 flex items-center gap-2">
                                    <Target size={14} /> {lang === 'es' ? 'Misión' : 'Mission'}
                                </span>
                            </button>

                            <button
                                onClick={() => setActiveTab('vision')}
                                className={`relative px-5 sm:px-6 py-2 rounded-xl font-black text-xs uppercase tracking-widest transition-all cursor-pointer flex items-center gap-2 ${
                                    activeTab === 'vision'
                                        ? 'text-white shadow-lg'
                                        : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                                }`}
                            >
                                {activeTab === 'vision' && (
                                    <motion.div
                                        layoutId="activeTabPill"
                                        className="absolute inset-0 bg-indigo-600 rounded-xl"
                                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10 flex items-center gap-2">
                                    <Compass size={14} /> {lang === 'es' ? 'Visión' : 'Vision'}
                                </span>
                            </button>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.3 }}
                            className={`p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border backdrop-blur-3xl text-left relative overflow-hidden ${
                                isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-xl shadow-blue-500/5'
                            }`}
                        >
                            <div className="max-w-3xl">
                                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-500 block mb-3">
                                    {activeTab === 'mission'
                                        ? (lang === 'es' ? 'NUESTRO PROPÓSITO OPERATIVO' : 'OPERATIONAL PURPOSE')
                                        : (lang === 'es' ? 'EL HORIZONTE QUE BUSCAMOS' : 'THE HORIZON WE PURSUE')}
                                </span>
                                <h3 className="text-2xl sm:text-3xl font-black mb-4 leading-tight">
                                    {activeTab === 'mission'
                                        ? (lang === 'es'
                                            ? 'Desarrollar tecnología útil que devuelva el control y el tiempo a las personas.'
                                            : 'Engineering practical tools that give time and agency back to individuals.')
                                        : (lang === 'es'
                                            ? 'Consolidar el ecosistema referente de software cívico y comercial independiente.'
                                            : 'Building Latin America’s leading independent civic and local commerce software suite.')}
                                </h3>
                                <p className="text-sm sm:text-base font-bold opacity-75 leading-relaxed">
                                    {activeTab === 'mission'
                                        ? (lang === 'es'
                                            ? 'Ponemos software móvil avanzado al servicio de causas concretas: seguridad ciudadana, selección de personal confiable, control financiero accesible y herramientas de supervivencia para el comercio tradicional.'
                                            : 'We place advanced mobile engineering at the service of tangible needs: public safety, reliable vetting, accessible finance, and competitive tools for traditional commerce.')
                                        : (lang === 'es'
                                            ? 'Demostrar que la innovación técnica y la rentabilidad son compatibles con un compromiso social inquebrantable, blindando la privacidad de las personas y defendiendo el valor de las economías locales.'
                                            : 'Proving that technological innovation can walk hand in hand with social responsibility, safeguarding privacy, and empowering neighborhood economies.')}
                                </p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* BENTO GRID DE INICIATIVAS */}
                <div className="mb-24 sm:mb-28 text-left">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                            {lang === 'es' ? 'QUÉ HACEMOS Y POR QUÉ' : 'WHAT WE SOLVE & WHY'}
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-8">
                        {lang === 'es' ? 'Soluciones Prácticas a Retos Cotidianos' : 'Practical Solutions to Real Hurdles'}
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                        <div className="lg:col-span-5 flex flex-col gap-3 sm:gap-4">
                            {pillars.map((p, idx) => {
                                const Icon = p.icon;
                                const isSelected = selectedPillar === idx;
                                return (
                                    <motion.div
                                        key={p.id}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => setSelectedPillar(idx)}
                                        className={`p-5 sm:p-6 rounded-[2rem] border text-left cursor-pointer transition-all flex items-center justify-between ${
                                            isSelected
                                                ? (isDark ? 'bg-slate-800/90 border-blue-500/60 shadow-xl' : 'bg-white border-blue-500 shadow-lg')
                                                : (isDark ? 'bg-slate-900/30 border-white/5 opacity-60 hover:opacity-100' : 'bg-slate-100/70 border-slate-200 opacity-70 hover:opacity-100')
                                        }`}
                                    >
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className={`p-3 rounded-2xl border shrink-0 ${
                                                isSelected
                                                    ? 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                                                    : 'bg-slate-500/10 border-transparent text-slate-400'
                                            }`}>
                                                <Icon size={20} />
                                            </div>
                                            <div className="truncate">
                                                <span className="text-[9px] font-black uppercase tracking-wider text-blue-500 block mb-1">
                                                    {p.tag}
                                                </span>
                                                <h4 className="text-sm sm:text-base font-black truncate">{p.title}</h4>
                                            </div>
                                        </div>
                                        <ChevronRight size={18} className={`shrink-0 transition-transform duration-300 ${
                                            isSelected ? 'rotate-90 text-blue-500' : 'text-slate-500'
                                        }`} />
                                    </motion.div>
                                );
                            })}
                        </div>

                        <div className="lg:col-span-7">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={selectedPillar}
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.98 }}
                                    transition={{ duration: 0.25 }}
                                    className={`h-full min-h-[360px] p-8 sm:p-12 rounded-[2.5rem] sm:rounded-[3rem] border backdrop-blur-3xl flex flex-col justify-between relative overflow-hidden bg-gradient-to-br ${
                                        pillars[selectedPillar].color
                                    } ${isDark ? 'border-white/10' : 'border-slate-200 shadow-xl'}`}
                                >
                                    <div>
                                        <div className="inline-block px-4 py-1.5 rounded-full bg-black/25 backdrop-blur-md border border-white/10 text-[11px] font-black uppercase tracking-widest mb-6">
                                            {pillars[selectedPillar].highlight}
                                        </div>
                                        <h3 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
                                            {pillars[selectedPillar].title}
                                        </h3>
                                        <p className="text-base sm:text-lg font-bold opacity-80 leading-relaxed max-w-xl">
                                            {pillars[selectedPillar].desc}
                                        </p>
                                    </div>

                                    <div className="pt-8 mt-8 border-t border-white/10 flex items-center justify-between">
                                        <span className="text-[10px] font-black uppercase tracking-widest opacity-60">
                                            TDSMX Platform Core
                                        </span>
                                        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-blue-400">
                                            <Eye size={14} /> {lang === 'es' ? 'Módulo Activo' : 'Active Module'}
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* GÉNESIS */}
                <div className="mb-24 sm:mb-28 text-left">
                    <div className="flex items-center gap-2 mb-3">
                        <Terminal size={16} className="text-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                            {lang === 'es' ? 'ORÍGENES & DESARROLLO' : 'ORIGIN & ROADMAP'}
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-8">
                        {lang === 'es' ? 'La Génesis de TDSMX' : 'The Genesis of TDSMX'}
                    </h2>

                    {/* VISTA MÓVIL (< md) */}
                    <div className="block md:hidden space-y-3">
                        {genesisTimeline.map((item, idx) => {
                            const isOpen = activeGenesis === idx;
                            return (
                                <div
                                    key={idx}
                                    className={`rounded-2xl border transition-all overflow-hidden ${
                                        isOpen
                                            ? (isDark ? 'bg-slate-900/90 border-blue-500/50 shadow-lg' : 'bg-white border-blue-500 shadow-md')
                                            : (isDark ? 'bg-slate-900/30 border-white/5 opacity-80' : 'bg-slate-100/80 border-slate-200')
                                    }`}
                                >
                                    <button
                                        onClick={() => setActiveGenesis(idx)}
                                        className="w-full p-4 flex items-center justify-between text-left"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl font-black text-blue-500">{item.year}</span>
                                            <div className="flex flex-col">
                                                <span className="text-[9px] font-black uppercase tracking-wider text-blue-400">
                                                    {item.phaseTag}
                                                </span>
                                                <span className="text-xs font-black uppercase tracking-tight">
                                                    {item.phase}
                                                </span>
                                            </div>
                                        </div>
                                        <ChevronDown size={18} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : 'opacity-40'}`} />
                                    </button>

                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.25 }}
                                                className="px-4 pb-5 pt-2 border-t border-current/10"
                                            >
                                                <h4 className="text-base font-black mb-2">{item.title}</h4>
                                                <p className="text-xs font-bold opacity-75 leading-relaxed mb-4">
                                                    {item.desc}
                                                </p>
                                                <div className="flex flex-wrap gap-2">
                                                    {item.specs.map((spec, sIdx) => (
                                                        <span
                                                            key={sIdx}
                                                            className="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                                        >
                                                            {spec}
                                                        </span>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>

                    {/* VISTA DESKTOP (>= md) */}
                    <div className={`hidden md:block rounded-[3rem] border overflow-hidden backdrop-blur-3xl ${
                        isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-xl'
                    }`}>
                        <div className="grid grid-cols-3 border-b border-white/10">
                            {genesisTimeline.map((item, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveGenesis(idx)}
                                    className={`py-6 px-6 text-left transition-all border-r last:border-r-0 border-white/10 relative cursor-pointer ${
                                        activeGenesis === idx
                                            ? (isDark ? 'bg-blue-600/15 text-white' : 'bg-blue-50 text-blue-900')
                                            : 'opacity-50 hover:opacity-90'
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-2xl font-black text-blue-500">{item.year}</span>
                                        <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                            {item.badge}
                                        </span>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-blue-400 block mb-0.5">
                                        {item.phaseTag}
                                    </span>
                                    <p className="text-xs font-black uppercase tracking-wide">
                                        {item.phase}
                                    </p>
                                    {activeGenesis === idx && (
                                        <motion.div
                                            layoutId="activeGenesisBarDesktop"
                                            className="absolute bottom-0 left-0 right-0 h-1 bg-blue-500"
                                        />
                                    )}
                                </button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeGenesis}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -15 }}
                                transition={{ duration: 0.25 }}
                                className="p-12 text-left"
                            >
                                <div className="max-w-3xl">
                                    <h3 className="text-3xl font-black mb-4">
                                        {genesisTimeline[activeGenesis].title}
                                    </h3>
                                    <p className="text-base font-bold opacity-75 leading-relaxed mb-8">
                                        {genesisTimeline[activeGenesis].desc}
                                    </p>

                                    <div className="flex flex-wrap gap-3">
                                        {genesisTimeline[activeGenesis].specs.map((spec, sIdx) => (
                                            <span
                                                key={sIdx}
                                                className="px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                            >
                                                {spec}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* FILOSOFÍA DE DISEÑO */}
                <div className="mb-24 sm:mb-28 text-left">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                            {lang === 'es' ? 'CÓDIGO ÉTICO' : 'ETHICAL CODE'}
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-8">
                        {lang === 'es' ? 'Nuestra Filosofía de Diseño' : 'Our Engineering Philosophy'}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {values.map((val, idx) => {
                            const Icon = val.icon;
                            return (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -6, transition: { duration: 0.2 } }}
                                    className={`p-7 rounded-[2rem] border backdrop-blur-xl flex flex-col justify-between transition-all ${
                                        isDark
                                            ? 'bg-slate-900/40 border-white/10 hover:border-blue-500/40'
                                            : 'bg-white border-slate-200 hover:border-blue-500/40 shadow-sm hover:shadow-md'
                                    }`}
                                >
                                    <div className="p-3 bg-blue-500/10 text-blue-500 rounded-2xl w-fit mb-5 border border-blue-500/20">
                                        <Icon size={22} />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-black mb-2 leading-snug">{val.title}</h3>
                                        <p className="text-xs font-bold opacity-60 leading-relaxed">{val.sub}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* LIDERAZGO */}
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-3">
                        <Scale size={16} className="text-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                            {lang === 'es' ? 'LIDERAZGO Y GOBERNANZA' : 'LEADERSHIP & ETHICS'}
                        </span>
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-8">
                        {lang === 'es' ? 'El Gran Equipo Detrás de TDSMX' : 'The Dedicated Team Behind TDSMX'}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                        {leadership.map((leader, idx) => (
                            <motion.div
                                key={idx}
                                whileHover={{ y: -4 }}
                                className={`rounded-[2.5rem] sm:rounded-[3rem] border overflow-hidden flex flex-col justify-between transition-all ${
                                    isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-xl shadow-blue-500/5'
                                }`}
                            >
                                <div>
                                    <div className="relative w-full h-72 sm:h-80 overflow-hidden bg-slate-800">
                                        <img
                                            src={leader.image}
                                            alt={leader.name}
                                            className="w-full h-full object-cover object-top"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = `
                                                    <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 p-6 text-center">
                                                        <span class="text-5xl font-black text-blue-500/40 mb-2">${leader.name.charAt(0)}</span>
                                                        <span class="text-xs font-black uppercase tracking-widest text-slate-400">${leader.name}</span>
                                                    </div>
                                                `;
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-transparent to-transparent opacity-90" />
                                        <div className="absolute top-4 right-4">
                                            <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full bg-black/50 backdrop-blur-md text-white border border-white/20">
                                                {leader.badge}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-6 sm:p-8">
                                        <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-1">
                                            {leader.name}
                                        </h3>
                                        <p className="text-xs font-black uppercase tracking-wider text-blue-500 mb-4">
                                            {leader.role}
                                        </p>
                                        <p className="text-xs sm:text-sm font-bold opacity-75 leading-relaxed mb-6">
                                            {leader.objective}
                                        </p>

                                        <div className={`p-4 rounded-2xl border flex items-start gap-3 ${
                                            isDark ? 'bg-white/5 border-white/5' : 'bg-blue-50/60 border-blue-100'
                                        }`}>
                                            <Quote size={18} className="text-blue-500 shrink-0 mt-0.5" />
                                            <p className="text-xs font-bold italic opacity-90 leading-relaxed">
                                                {leader.motto}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-6 sm:px-8 py-4 border-t border-white/5 flex items-center justify-between">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-500">
                                        Liderazgo Activo
                                    </span>
                                    <span className="text-[10px] font-black uppercase tracking-widest opacity-40">
                                        TDSMX Ventures
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AboutUs;